import React, { ChangeEvent, FC, FocusEventHandler, SyntheticEvent, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { createFullCreditInvoiceTemplate } from 'apollo/templates/invoices';
import { ApolloError } from '@apollo/client/errors';
import { Invoice, InvoiceDelivery, MonetaryTotal } from 'models/invoice';
import { formatCurrency, getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import Loader from 'components/shared/loader/loader.component';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { ApolloCache, Reference } from '@apollo/client/cache';
import { writeInvoiceToCache } from 'apollo/cache/invoice';
import { Link as RouterLink } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

type FullCreditInvoice = {
    invoiceDate: Date;
    paymentTerm: number;
    invoiceNumber?: string;
    debitInvoiceId: string;
    shouldSendEmails: boolean;
    shouldSendElectronicInvoice?: boolean;
};

type CreditInvoiceDialogProps = {
    open: boolean;
    debitInvoice: Invoice;
    clinicName: string;
    paymentTerms: number;
    deliveryMethod: InvoiceDelivery;
    total: MonetaryTotal;
    onDialogClose: (error?: ApolloError, invoice?: Invoice) => void;
};

const CreditInvoiceDialog: FC<CreditInvoiceDialogProps> = ({
    open,
    onDialogClose,
    debitInvoice,
    clinicName,
    deliveryMethod,
    paymentTerms,
    total,
}: CreditInvoiceDialogProps) => {
    const { t: translate } = useTranslation(['common', 'invoices', 'orders', 'client-directory']);
    const errorTexts: Record<string, string> = {
        invoiceDate: translate('common:VALIDATION:INVOICE_DATE'),
        invoiceNumber: translate('common:VALIDATION:INVOICE_NUMBER'),
        paymentTerm: translate('client-directory:VALIDATION:PAYMENT_TERMS'),
    };

    const electronicBilling =
        deliveryMethod === InvoiceDelivery.SCHEDULED_ELECTRONIC ||
        deliveryMethod === InvoiceDelivery.ELECTRONIC_BILLING;
    const deliveryMethodEmail = deliveryMethod === InvoiceDelivery.EMAIL;
    const [creditInvoice, setCreditInvoice] = useState<FullCreditInvoice>({
        invoiceDate: new Date(),
        debitInvoiceId: '',
        invoiceNumber: '',
        paymentTerm: 0,
        shouldSendEmails: deliveryMethodEmail,
        shouldSendElectronicInvoice: electronicBilling,
    } as FullCreditInvoice);

    useEffect(() => {
        setCreditInvoice((previous) => ({
            ...previous,
            paymentTerm: paymentTerms,
            debitInvoiceId: debitInvoice._id,
            shouldSendEmails: deliveryMethodEmail,
            shouldSendElectronicInvoice: electronicBilling,
        }));
    }, [paymentTerms, debitInvoice._id, electronicBilling, deliveryMethodEmail]);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const apolloClient = useApolloClient();

    const currentOrg = currentOrgVar();
    const invoiceNumberType = currentOrg?.invoicing?.configuration?.invoiceNumberType;

    const [createFullCreditInvoice, { loading }] = useMutation(createFullCreditInvoiceTemplate, {
        onCompleted: ({ createFullCreditInvoice: invoice }) => {
            const dInvoice = { ...debitInvoice, isCredited: true };
            dInvoice.creditInvoices = [...(dInvoice.creditInvoices || [])];
            dInvoice.creditInvoices.push({ ...invoice });
            writeInvoiceToCache(apolloClient, dInvoice);
            onDialogClose(undefined, invoice);
        },
        onError: (error: ApolloError) => {
            onDialogClose(error);
        },
        update(cache: ApolloCache<Invoice[]>, { data: { createFullCreditInvoice: invoice } }) {
            cache.modify({
                fields: {
                    invoices(invoiceRefs: Reference | (Reference | undefined)[], { toReference }) {
                        const newInvoicesRef = toReference(invoice);
                        if (invoiceRefs instanceof Array) {
                            return [newInvoicesRef, ...invoiceRefs];
                        }
                        return [newInvoicesRef, invoiceRefs];
                    },
                },
            });
        },
    });

    const validate = (values: FullCreditInvoice) => {
        if (!values.invoiceDate) {
            return true;
        }
        if (!values.paymentTerm) {
            return true;
        }
        if (invoiceNumberType === 'MANUAL' && !values.invoiceNumber) {
            return true;
        }
        return false;
    };

    const handleClick = () => {
        createFullCreditInvoice({ variables: { fullCreditInvoice: creditInvoice } });
    };

    const handleInvoiceDateChange = (date: Date | null) => {
        if (!date) {
            setErrors({ ...errors, invoiceDate: errorTexts['invoiceDate'] });
        } else {
            setErrors({ ...errors, invoiceDate: '' });
        }
        setCreditInvoice({ ...creditInvoice, invoiceDate: date || new Date() });
    };

    const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const name = event.target.name as string;
        if (!value) {
            setErrors({ ...errors, [name]: errorTexts[name] });
        } else {
            setErrors({ ...errors, [name]: '' });
        }
        setCreditInvoice({ ...creditInvoice, [event.target.name]: value });
    };

    const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event: SyntheticEvent) => {
        const target = event.target as HTMLInputElement;
        const name = target.name;
        const value = target.value;
        if (!value) {
            setErrors({ ...errors, [name]: errorTexts[name] });
        } else {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handlePaymentTermChange = (paymentTerm: string) => {
        if (!paymentTerm) {
            setErrors({ ...errors, paymentTerm: translate('client-directory:VALIDATION:PAYMENT_TERMS') });
        }
        setCreditInvoice({ ...creditInvoice, paymentTerm: parseInt(paymentTerm) });
    };

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, name: string) => {
        setCreditInvoice({ ...creditInvoice, [name]: event.target.checked });
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={() => {
                onDialogClose();
            }}
        >
            <DialogTitle>{translate('invoices:CREATE_CREDIT_INVOICE')}</DialogTitle>
            <DialogContent>
                <Box mt={1}>{translate('invoices:CREATE_CREDIT_INVOICE_DESCRIPTION')}</Box>
                <Box mt={2}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} lg={4}>
                            <Typography variant="subtitle2">{translate('invoices:INVOICE_NUMBER')}:</Typography>
                            <Box>{debitInvoice.invoiceNumber}</Box>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Typography variant="subtitle2">{translate('common:CLINIC')}:</Typography>
                            <Box>{clinicName}</Box>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Typography variant="subtitle2">{translate('orders:TOTAL_INCL_VAT')}:</Typography>
                            <Box>{formatCurrency(total?.totalIncVat || 0, total?.currency)}</Box>
                        </Grid>
                    </Grid>
                </Box>
                <Box mt={2}>
                    <Divider />
                </Box>
                <Box mt={2}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} lg={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                                <FormControl margin="dense" fullWidth>
                                    <DatePicker
                                        format={getDatePickerFormat()}
                                        minDate={new Date()}
                                        onChange={handleInvoiceDateChange}
                                        label={translate('invoices:INVOICE_DATE')}
                                        value={creditInvoice.invoiceDate}
                                    />
                                </FormControl>
                            </LocalizationProvider>
                        </Grid>
                        {invoiceNumberType === 'MANUAL' ? (
                            <Grid item xs={12} lg={4}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        label={translate('invoices:CREDIT_INVOICE_NUMBER')}
                                        name="invoiceNumber"
                                        onBlur={handleBlur}
                                        onChange={handleFieldChange}
                                        value={creditInvoice.invoiceNumber}
                                        required
                                        helperText={errors.invoiceNumber}
                                        error={Boolean(errors.invoiceNumber)}
                                    />
                                </FormControl>
                            </Grid>
                        ) : undefined}
                        <Grid item xs={12} lg={4}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    onBlur={handleBlur}
                                    onValueChange={handlePaymentTermChange}
                                    value={String(creditInvoice.paymentTerm || 0)}
                                    name="paymentTerm"
                                    helperText={errors.paymentTerm}
                                    error={Boolean(errors.paymentTerm)}
                                    label={translate('invoices:PAYMENT_TERM')}
                                    hasDecimal={false}
                                    maxLength={4}
                                    inputProps={{ style: { textAlign: 'right' } }}
                                />
                            </FormControl>
                        </Grid>
                        {deliveryMethodEmail ? (
                            <Grid item xs={12} lg={4}>
                                <FormControl margin="dense" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={creditInvoice.shouldSendEmails}
                                                onChange={(event) => handleCheckboxChange(event, 'shouldSendEmails')}
                                            />
                                        }
                                        label={translate('invoices:SEND_EMAIL_AUTOMATICALLY')}
                                    />
                                </FormControl>
                            </Grid>
                        ) : undefined}

                        {electronicBilling ? (
                            <Grid item xs={12} lg={4}>
                                <FormControl margin="dense" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={creditInvoice.shouldSendElectronicInvoice}
                                                onChange={(event) =>
                                                    handleCheckboxChange(event, 'shouldSendElectronicInvoice')
                                                }
                                            />
                                        }
                                        label={translate('invoices:SEND_ELECTRONIC_INVOICE')}
                                    />
                                </FormControl>
                            </Grid>
                        ) : undefined}
                    </Grid>
                </Box>
                <Box mt={2}>
                    <Alert severity="info">
                        {translate('invoices:CREATE_CREDIT_INVOICE_INFO')}&nbsp;{' '}
                        <Link component={RouterLink} to="/invoices/debit-credit-notes">
                            {translate('invoices:NAVIGATE_TO_CREDIT_NOTES')}
                        </Link>
                    </Alert>
                </Box>
            </DialogContent>
            {loading ? (
                <Loader />
            ) : (
                <DialogActions>
                    <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                    <Button
                        variant="contained"
                        type="button"
                        onClick={handleClick}
                        color="error"
                        disabled={validate(creditInvoice)}
                    >
                        {translate('invoices:CREATE_CREDIT_INVOICE')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default CreditInvoiceDialog;
